import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { IP_FAQ_CATEGORIES } from "@/content/ip-corpus/faq-defs";

export const Route = createFileRoute("/en/ip/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/ip-corpus");
    return { faqs: m.IP_FAQ_EN };
  },
  component: EnIpFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Intellectual Property in Thailand — ${faqs.length} FAQs`,
        description:
          "Trademark registration, Madrid Protocol international filings, opposition and appeal, copyright and recordation, invention and petty patents, design patents, trade secrets, geographical indications, powers of attorney and certified translations.",
        path: "/en/ip/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/ip/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Intellectual Property FAQ", url: `${site}/en/ip/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/ip/faq")) },
      ],
    };
  },
});

function EnIpFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Intellectual Property FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Intellectual Property in Thailand: Trademarks, Copyright, Patents, Trade Secrets and Supporting Documents
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers grounded in the Department of Intellectual Property, the Trademark Act B.E. 2534, the
          Copyright Act B.E. 2537, the Patent Act B.E. 2522, the Trade Secrets Act B.E. 2545 and the Geographical
          Indications Protection Act B.E. 2546, together with the Madrid Protocol and the Patent Cooperation Treaty.
          Information is approximate and current as at August 2026; confirm with the authority before filing.
        </p>

        <section aria-label="Topics" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by topic</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {IP_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/ip/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Have our team review your IP filing pack first</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the filing type, the applicant's country and your deadline — by phone, LINE or email, Monday to
            Saturday 9:00–18:00.
          </p>
          <a href="/en/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <FaqSourceNote topic="ip" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
